import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import { sendNumberVerification } from 'flow/actions/extension-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import Button from 'flow/components/ui-components/Button';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import { NumberState } from 'flow/utils/extension-utils';
import {
    getProfileCallerId,
    getProfileFromProfileList,
    hasLandlineCallerId,
    updateFixedProfile,
    updateMobileProfile,
    updateSMSSenderName,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from '../Page';
import SettingSection from '../SettingSection';
import CallerIdCombobox, { CallerIdOption } from './CallerId/CallerIdCombobox';
import SmsNumberCombobox, {
    SmsNumberOption,
} from './CallerId/SmsNumberCombobox';
import { selectHasCapability } from 'flow/selectors/ExtensionSelectors';
import { Capabilities } from 'flow/utils/capability-utils';

const ProfileCallerId = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();
    const hasCapabilitySms = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.SMS),
    );
    const { contact } = useContact(extensionKey);
    const { extension } = useExtension(extensionKey);
    const { profiles } = useProfiles(extensionKey);

    const isTechnicalUser = useAppSelector((state) =>
        state.extensions.hasIn([
            'extensions',
            extensionKey,
            'contact',
            'technicalNumber',
        ]),
    );

    const [fixedNumber, setFixedNumber] = useState<CallerIdOption>();
    const [mobileNumber, setMobileNumber] = useState<CallerIdOption>();
    const [smsNumber, setSmsNumber] = useState<SmsNumberOption>();

    const profile = getProfileFromProfileList(profileKey, profiles);

    const getCallerId = (callerIdType: string, mainNumber: string) => {
        const refCallerIdType = profile.getIn(['callerId', callerIdType]);

        if (refCallerIdType === 'mainanumber') {
            return profile.getIn(['callerId', mainNumber, 'key']);
        }

        return refCallerIdType;
    };

    useEffect(() => {
        if (profile) {
            const newFixedNumber = getProfileCallerId(
                i18next,
                profile,
                contact,
                'fixedCallerIdType',
                'fixedMainNumber',
            );

            setFixedNumber({
                id: getCallerId('fixedCallerIdType', 'fixedMainNumber'),
                name: newFixedNumber,
            });

            const newMobileNumber = getProfileCallerId(
                i18next,
                profile,
                contact,
                'mexCallerIdType',
                'mexMainNumber',
            );

            setMobileNumber({
                id: getCallerId('mexCallerIdType', 'mexMainNumber'),
                name: newMobileNumber,
            });

            setSmsNumber({
                id: profile.getIn(['smsSenderNameId', 'key']),
                name: profile.getIn(['smsSenderNameId', 'description']),
            });
        }
    }, [profile]);

    if (!profile || !contact) {
        return <Loading />;
    }

    const showLandline =
        (profile.getIn(['callerId', 'callerIdOptions']).includes('fixed') ||
            isTechnicalUser) &&
        hasLandlineCallerId(profile);

    const onChangeCallerId = (
        numberType: 'fixed' | 'msisdn',
        newValue: CallerIdOption,
    ) => {
        const updateNumberProfile =
            numberType === 'fixed' ? updateFixedProfile : updateMobileProfile;

        dispatch(
            updateProfile(
                extensionKey,
                updateNumberProfile(profile, newValue.id),
            ),
        );
    };

    const onChangeSmsSender = (option: SmsNumberOption) => {
        const updatedProfile = updateSMSSenderName(profile, option.id);

        dispatch(updateProfile(extensionKey, updatedProfile));
    };

    const handleConfirmation = () => {
        openModal('confirmationDialog', {
            title: i18next.t('Verify.Dialog.Title'),
            description: i18next.t('Verify.Dialog.Description'),
            handleAccept: () => {
                dispatch(
                    sendNumberVerification(extensionKey, contact.get('mobile')),
                );
            },
        });
    };

    const getMobileStatus = () => {
        const numberStatus = (() => {
            switch (extension.get('mobileStatus', 'mobileNumberState')) {
                case NumberState.nonverified.key:
                    return (
                        <div className="flex items-center gap-2">
                            {i18next.t(
                                'settings.callerid.sms.edit.nonverified',
                            )}
                            <Button
                                onClick={handleConfirmation}
                                color="primary"
                                className="inline-block"
                            >
                                {i18next.t('settings.contact.verify.number')}
                            </Button>
                        </div>
                    );
                case NumberState.numbermissing.key:
                    return i18next.t(
                        'settings.callerid.sms.edit.numbermissing',
                    );
                default:
                    return null;
            }
        })();

        if (numberStatus) {
            return <SettingSection>{numberStatus}</SettingSection>;
        }

        return null;
    };

    return (
        <Page
            header="settings.callerid"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <div className="flex flex-col gap-6 divide-y">
                {showLandline ? (
                    <div className="pb-6">
                        <SettingSection className="text-sm/4">
                            {i18next.t('settings.landline')}
                        </SettingSection>

                        <p className="mb-2 px-2 text-sm">
                            {i18next.t('settings.number_present_fixed')}
                        </p>

                        <CallerIdCombobox
                            ariaLabel={i18next.t(
                                'settings.number_present_fixed',
                            )}
                            caller={fixedNumber}
                            onChange={(newValue) =>
                                onChangeCallerId('fixed', newValue)
                            }
                            enableMobileOption={profile.getIn([
                                'callerId',
                                'canShowMobileCallerIdFromFixed',
                            ])}
                            editable={profile.get('editable')}
                        />
                    </div>
                ) : null}

                {profile
                    .getIn(['callerId', 'callerIdOptions'])
                    .includes('msisdn') && (
                    <div className="pb-6">
                        <SettingSection className="text-sm/4">
                            {i18next.t('settings.mobile')}
                        </SettingSection>

                        <p className="mb-2 px-2 text-sm">
                            {i18next.t('settings.number_present_mobile')}
                        </p>

                        <CallerIdCombobox
                            ariaLabel={i18next.t(
                                'settings.number_present_mobile',
                            )}
                            caller={mobileNumber}
                            onChange={(newValue) =>
                                onChangeCallerId('msisdn', newValue)
                            }
                            editable={profile.get('editable')}
                        />
                    </div>
                )}

                {profile.get('smsSenderNameId') && hasCapabilitySms ? (
                    <div>
                        <SettingSection className="text-sm/4">
                            {i18next.t('settings.callerid.sms.title')}
                        </SettingSection>

                        <p className="mb-2 px-2 text-sm">
                            {i18next.t('settings.callerid.sms.description')}
                        </p>

                        <SmsNumberCombobox
                            ariaLabel={i18next.t(
                                'settings.callerid.sms.description',
                            )}
                            extensionKey={extensionKey}
                            caller={smsNumber}
                            onChange={onChangeSmsSender}
                            editable={profile.get('editable')}
                        />

                        {getMobileStatus()}
                    </div>
                ) : null}
            </div>
        </Page>
    );
};

export default ProfileCallerId;
